// Static storytelling content for the portfolio.
// CONTENT holds every piece of user-facing text in English (en) and Spanish (es).
// A handful of exports below CONTENT stay language-independent because they are
// used as stable keys (document categories in the DB) or are admin-only.

export const PROFILE = {
  name: "Florencia Rombolá",
  role: "People Operations & AI Automation Specialist",
  email: "mfrombola@hotmail.com",
  linkedin: "https://www.linkedin.com/in/florencia-rombolá-/",
  location: "Valencia, Spain",
  cvUrl: "/MFR_People_Ops.pdf",
  portrait: "/portrait.jpg",
};

// Stable canonical keys for "My Work" categories. These double as the value
// stored on each document in the database (see backend `documents.category`),
// so they must stay in English and unchanged even though the displayed title
// is translated. Admin.jsx also uses this list directly.
export const WORK_CATEGORY_KEYS = [
  "People Strategy",
  "Organizational Design",
  "Workforce Planning",
  "Performance Management",
  "Employee Experience",
  "HR Technology",
  "People Analytics",
  "Leadership & Change",
  "AI Product Design",
];

// Stable canonical keys for Knowledge Hub topics (also stored as `category` on
// knowledge documents). Admin.jsx uses this list directly.
export const KNOWLEDGE_TOPIC_KEYS = [
  "People Strategy", "Performance Management", "Workforce Planning", "Organizational Design",
  "Employee Experience", "HR Technology", "Artificial Intelligence", "Product Design",
  "Research Notes", "Templates", "Playbooks", "Case Studies", "Presentations",
];

// Admin-only, not shown on the public site, so left in English.
export const ASSET_TYPES = ["PDF", "Presentation", "Template", "Framework", "Policy", "Diagram", "Image"];
export const PRODUCT_DOC_CATEGORIES = [
  "Architecture decisions", "User flows", "Wireframes", "Roadmap", "Research",
  "Product specifications", "Technical documentation", "Meeting notes", "Future ideas",
];

// ---------------------------------------------------------------------------
// Career timeline: roles and projects (replaces the old category-nested My Work
// data). ROLES are ordered most recent first (reverse-chronological), matching
// resume convention. PROJECTS reference a role by id and can carry more than
// one category tag. WORK_CATEGORY_KEYS above stays in place only for Admin's
// document/asset categorization, unrelated to this list now.
// ---------------------------------------------------------------------------

export const CAREER_ROLES = [
  {
    "id": "R7",
    "company": "AI-Native HR Platform",
    "title": "Independent Project, Product Owner",
    "start": "2025",
    "end": "present",
    "industry": {
      "en": "Independent, HR Tech SaaS, built in parallel with full-time roles",
      "es": "Independiente, SaaS de tecnología de RRHH, desarrollado en paralelo a sus roles a tiempo completo"
    },
    "description": {
      "en": "Defines the product vision, strategy and roadmap for an agentic AI platform built to support multiple HR functions, leading development of its first, ATS-agnostic module from concept through MVP. Designs the AI agent workflows and orchestration model at the platform's core, translating HR needs into product requirements and scalable solutions in close collaboration with technology and design.",
      "es": "Defino la visión, estrategia y roadmap de producto de una plataforma de IA agéntica pensada para dar soporte a múltiples funciones de RRHH, liderando el desarrollo de su primer módulo, agnóstico de ATS, desde el concepto hasta el MVP. Diseño los flujos de agentes de IA y el modelo de orquestación que son el corazón de la plataforma, traduciendo necesidades de RRHH en requerimientos de producto y soluciones escalables en conjunto con tecnología y diseño."
    }
  },
  {
    "id": "R6",
    "company": "Oasis FZCO",
    "title": "People Communications & Employee Experience Designer",
    "start": "Jan 2026",
    "end": "Jul 2026",
    "industry": {
      "en": "B2B iGaming Tech, Remote, 2 countries",
      "es": "Tecnología B2B iGaming, Remoto, 2 países"
    },
    "description": {
      "en": "Designed and implemented foundational people processes for a 16-person startup preparing to double in size within a year, spanning engagement, performance, communication, process implementation, technology and employee experience, grounded in compliance and alignment with company culture to enable fast, scalable growth.",
      "es": "Diseñé e implementé procesos fundacionales para una startup de 16 personas que se preparaba para duplicar su tamaño en un año, abarcando engagement, desempeño, comunicación, implementación de procesos, tecnología y experiencia del empleado, apoyada en compliance y alineación con la cultura de la compañía para habilitar un crecimiento rápido y escalable."
    }
  },
  {
    "id": "R5",
    "company": "WA Technology",
    "title": "People Tech & Solution Manager",
    "start": "Jan 2024",
    "end": "Sep 2025",
    "industry": {
      "en": "B2B iGaming Tech, Remote, 8 countries",
      "es": "Tecnología B2B iGaming, Remoto, 8 países"
    },
    "description": {
      "en": "Led the People technology strategy and roadmap, owning the end-to-end design, implementation and adoption of new HR systems and processes to support scalability and efficiency. Drove automation and digitalization of People operations, led change management for each rollout, and established the governance and continuous improvement practices that matured the People function as the company grew.",
      "es": "Lideré la estrategia y el roadmap de tecnología de Personas, siendo dueña del diseño, la implementación y la adopción de nuevos sistemas y procesos de RRHH de punta a punta para sostener la escalabilidad y la eficiencia. Impulsé la automatización y digitalización de las operaciones de Personas, lideré la gestión del cambio en cada implementación, y establecí la gobernanza y las prácticas de mejora continua que hicieron madurar la función a medida que la compañía crecía."
    }
  },
  {
    "id": "R4",
    "company": "WA Technology",
    "title": "Global HRBP",
    "start": "Jan 2023",
    "end": "Dec 2023",
    "industry": {
      "en": "B2B iGaming Tech, Remote, 8 countries",
      "es": "Tecnología B2B iGaming, Remoto, 8 países"
    },
    "description": {
      "en": "Acted as a strategic People partner to leaders through a period of rapid growth and organizational change, building and implementing People processes, policies and frameworks suited to a scaling technology environment. Partnered on organizational design, workforce planning, performance and recruitment for key roles, and led People due diligence and integration planning through the company's acquisitions and market integrations.",
      "es": "Actué como socia estratégica de Personas para los líderes durante un período de crecimiento acelerado y cambio organizacional, construyendo e implementando procesos, políticas y marcos adaptados a un entorno tecnológico en escalamiento. Acompañé el diseño organizacional, la planificación de personal, el desempeño y el reclutamiento de posiciones clave, y lideré la due diligence de Personas y la planificación de integración en las adquisiciones y expansiones de mercado de la compañía."
    }
  },
  {
    "id": "R3",
    "company": "Bayer Life Science",
    "title": "Talent Advisor Sr.",
    "start": "Jan 2021",
    "end": "Dec 2021",
    "industry": {
      "en": "Life Science / Pharma, Cono Sur",
      "es": "Ciencias de la Vida / Farma, Cono Sur"
    },
    "description": {
      "en": "Served as a trusted People advisor to managers and employees across the full employee lifecycle, partnering on employee relations, performance and organizational matters while ensuring consistent application of Bayer's global and regional People policies. Coordinated with specialist People teams to deliver initiatives locally and fed local insights back into regional and global People strategy.",
      "es": "Fui asesora de confianza para managers y empleados a lo largo de todo el ciclo de vida del empleado, acompañando en relaciones laborales, desempeño y temas organizacionales, y asegurando la aplicación consistente de las políticas globales y regionales de Bayer. Coordiné con los equipos especialistas de Personas para entregar las iniciativas a nivel local y devolví esos aprendizajes a la estrategia regional y global."
    }
  },
  {
    "id": "R2",
    "company": "Bayer Crop Science",
    "title": "People Partner Manager",
    "start": "Nov 2016",
    "end": "Dec 2020",
    "industry": {
      "en": "Crop Science / Agribiotech, Brazil / LATAM",
      "es": "Agroindustria / Agrobiotecnología, Brasil / LATAM"
    },
    "description": {
      "en": "Led the People partnership for plant and field operations, balancing seasonal labor demand, cost control and operational goals for a workforce of 3,000+ seasonal employees, while partnering with union and business leaders to keep HR practices aligned with the operation's needs.",
      "es": "Lideré la relación de Personas para operaciones de planta y campo, equilibrando la demanda estacional de mano de obra, el control de costos y los objetivos operativos para una fuerza de más de 3.000 empleados de temporada, mientras trabajaba junto a sindicatos y líderes de negocio para mantener las prácticas de RRHH alineadas con las necesidades de la operación."
    }
  },
  {
    "id": "R1",
    "company": "AB InBev",
    "title": "LATAM Payroll & Personnel Administration Lead",
    "start": "Jul 2011",
    "end": "Jun 2015",
    "industry": {
      "en": "FMCG, 7 countries LATAM",
      "es": "FMCG, 7 países de LATAM"
    },
    "description": {
      "en": "Led a 15-person payroll team across 7 LATAM countries, delivering accurate, compliant payroll for approximately 10,000 employees with 98% SLA accuracy. Owned labor, tax and social security compliance by country, standardized processes and automated the HRIS to reduce cost and manual effort, and acted as the regional liaison between Finance, Tax, Legal and the business.",
      "es": "Lideré un equipo de 15 personas en 7 países de LATAM, entregando nómina precisa y conforme a la normativa para unas 10.000 personas con 98% de exactitud en el SLA. Tuve bajo mi responsabilidad el cumplimiento laboral, fiscal y de seguridad social por país, estandaricé procesos y automaticé el HRIS para reducir costo y esfuerzo manual, y fui el enlace regional entre Finanzas, Impuestos, Legal y el negocio."
    }
  }
];

export const CAREER_PROJECTS = [
  {
    "id": "sap-stabilization",
    "categories": [
      "HR Technology"
    ],
    "role": "R1",
    "year": "2011-2012",
    "flagship": false,
    "name": {
      "en": "SAP Stabilization & Data Cleansing",
      "es": "Estabilización y Saneamiento de Datos de SAP"
    },
    "roleTag": {
      "en": "Led",
      "es": "Lideré"
    },
    "description": {
      "en": "Joined after SAP had already gone live to lead the post-implementation stabilization phase, cleansing and correcting payroll and personnel data to bring the system to a reliable, audit-ready state.",
      "es": "Ingresé una vez que SAP ya estaba implementado para liderar la etapa de estabilización posterior al lanzamiento, saneando y corrigiendo los datos de nómina y personal para llevar el sistema a un estado confiable y auditable."
    }
  },
  {
    "id": "digital-payslips",
    "categories": [
      "HR Technology",
      "Leadership & Change"
    ],
    "role": "R1",
    "year": "2012",
    "flagship": false,
    "name": {
      "en": "Digital Payslips Rollout",
      "es": "Implementación de Recibos Digitales"
    },
    "roleTag": {
      "en": "Led",
      "es": "Lideré"
    },
    "description": {
      "en": "Led the rollout of digital payslips, a pioneering practice in the Argentine market at the time — the company became the third in Argentina to adopt the model, replacing paper-based delivery for the full employee population.",
      "es": "Lideré la implementación de recibos digitales, una práctica pionera en el mercado argentino en ese momento: la compañía fue la tercera en Argentina en adoptar el modelo, reemplazando la entrega en papel para toda la dotación de empleados."
    }
  },
  {
    "id": "ecuador-peru-payroll-integration",
    "categories": [
      "Workforce Planning",
      "HR Technology"
    ],
    "role": "R1",
    "year": "2013",
    "flagship": false,
    "name": {
      "en": "Ecuador & Peru Payroll Integration",
      "es": "Integración de Nómina de Ecuador y Perú"
    },
    "roleTag": {
      "en": "Led",
      "es": "Lideré"
    },
    "description": {
      "en": "Led the integration of Ecuador and Peru payroll operations into the LATAM headcount and payroll administration scope, extending regional coverage and standardizing reporting across the two new countries.",
      "es": "Lideré la integración de las operaciones de nómina de Ecuador y Perú al alcance de headcount y administración de nómina de LATAM, extendiendo la cobertura regional y estandarizando el reporting en los dos países nuevos."
    }
  },
  {
    "id": "global-people-strategy",
    "categories": [
      "People Strategy",
      "Workforce Planning",
      "HR Technology"
    ],
    "role": "R4",
    "year": "2023",
    "flagship": true,
    "name": {
      "en": "Global People Strategy",
      "es": "Estrategia Global de Personas"
    },
    "roleTag": {
      "en": "Co-designed",
      "es": "Codiseñé"
    },
    "description": {
      "en": "Co-designed the 2023 People strategy for a team scaling from 60 to 300 people across 8 countries, built the Jira-based roadmap tracking tool for continuous leadership visibility into priorities, and supported the Talent Acquisition team in building the workforce planning and recruitment capacity framework, connecting business strategy, hiring demand and financial planning, with a hiring tracker and dashboard that let the team manage 40+ open roles at once.",
      "es": "Codiseñé la estrategia de Personas 2023 para un equipo que escaló de 60 a 300 personas en 8 países, desarrollé la herramienta de seguimiento de roadmap en Jira para dar al liderazgo visibilidad continua sobre las prioridades, y di soporte al equipo de Talent Acquisition en la construcción del marco de planificación de personal y capacidad de reclutamiento, conectando estrategia de negocio, demanda de contratación y planificación financiera, con un tracker de contrataciones y un dashboard que le permitió al equipo gestionar más de 40 posiciones abiertas a la vez."
    }
  },
  {
    "id": "hr-operating-model-redesign",
    "categories": [
      "People Strategy",
      "Organizational Design"
    ],
    "role": "R4",
    "year": "2023",
    "flagship": false,
    "name": {
      "en": "HR Operating Model Redesign",
      "es": "Rediseño del Modelo Operativo de RRHH"
    },
    "roleTag": {
      "en": "Co-led",
      "es": "Colideré"
    },
    "description": {
      "en": "Co-designed the HR operating model to support sustainable scaling, separating Ops, BP and TA, redefining the HRBP role and creating a dedicated People Ops team to handle day-to-day operations and free up BPs for strategic work, supporting a team growing at 150% YoY.",
      "es": "Codiseñé el modelo operativo de RRHH para sostener un crecimiento sustentable, separando Ops, BP y TA, redefiniendo el rol de HRBP y creando un equipo dedicado de People Ops para las operaciones diarias y liberar a los BP para trabajo estratégico, en un equipo creciendo al 150% interanual."
    }
  },
  {
    "id": "people-policies-wa",
    "categories": [
      "People Strategy"
    ],
    "role": "R4",
    "year": "2023-2025",
    "flagship": false,
    "name": {
      "en": "People Policies & Governance (WA Technology)",
      "es": "Políticas y Gobierno de Personas (WA Technology)"
    },
    "roleTag": {
      "en": "Designed",
      "es": "Diseñé"
    },
    "description": {
      "en": "Designed and implemented core People policies from scratch, including AML compliance, grievance and disciplinary processes, the People handbook, PTO, Annual Salary Review, Profit Sharing Scheme and Equipment Policy, adapted to multiple locations and employment models.",
      "es": "Diseñé e implementé desde cero las políticas centrales de Personas, incluyendo cumplimiento AML, procesos de queja y disciplinarios, el manual del empleado, PTO, revisión salarial anual, esquema de reparto de utilidades y política de equipamiento, adaptadas a múltiples ubicaciones y modelos de contratación."
    }
  },
  {
    "id": "people-policies-oasis",
    "categories": [
      "People Strategy"
    ],
    "role": "R6",
    "year": "2026",
    "flagship": false,
    "name": {
      "en": "People Policies & Governance (Oasis FZCO)",
      "es": "Políticas y Gobierno de Personas (Oasis FZCO)"
    },
    "roleTag": {
      "en": "Implemented",
      "es": "Implementé"
    },
    "description": {
      "en": "Rolled out and communicated core People policies for the organization, adapting existing frameworks to a smaller, fast-moving team.",
      "es": "Implementé y comuniqué las políticas centrales de Personas de la organización, adaptando marcos existentes a un equipo más chico y de ritmo acelerado."
    }
  },
  {
    "id": "europe-brazil-integration",
    "categories": [
      "Organizational Design",
      "Leadership & Change"
    ],
    "role": "R4",
    "year": "2023",
    "flagship": false,
    "name": {
      "en": "Europe & Brazil Integration",
      "es": "Integración de Europa y Brasil"
    },
    "roleTag": {
      "en": "Led",
      "es": "Lideré"
    },
    "description": {
      "en": "Led the org design and change management workstream for the Europe (Portugal, Bulgaria, Malta) and Brazil market integrations, reviewing structures, identifying overlaps, redefining roles and reporting lines, and designing the communication plan to support leaders through the transition.",
      "es": "Lideré el frente de diseño organizacional y gestión del cambio de las integraciones de mercado en Europa (Portugal, Bulgaria, Malta) y Brasil, revisando estructuras, identificando superposiciones, redefiniendo roles y líneas de reporte, y diseñando el plan de comunicación para acompañar a los líderes durante la transición."
    }
  },
  {
    "id": "mexico-latam-integration",
    "categories": [
      "Organizational Design",
      "Leadership & Change"
    ],
    "role": "R5",
    "year": "2024",
    "flagship": false,
    "name": {
      "en": "Mexico & LATAM Integration",
      "es": "Integración de México y LATAM"
    },
    "roleTag": {
      "en": "Supported",
      "es": "Acompañé"
    },
    "description": {
      "en": "Supported the Mexico and LATAM market integration from a systems and administration standpoint, helping align structures and processes as the region joined the organization.",
      "es": "Acompañé la integración de mercado de México y LATAM desde una perspectiva de sistemas y administración, ayudando a alinear estructuras y procesos a medida que la región se incorporaba a la organización."
    }
  },
  {
    "id": "bayer-ls-hr-transformation",
    "categories": [
      "Organizational Design",
      "Leadership & Change"
    ],
    "role": "R3",
    "year": "2021",
    "flagship": false,
    "name": {
      "en": "Bayer Life Science HR Operating Model Transformation",
      "es": "Transformación del Modelo Operativo de RRHH en Bayer Life Science"
    },
    "roleTag": {
      "en": "Contributed",
      "es": "Contribuí"
    },
    "description": {
      "en": "Contributed to the redesign of the HR operating model and value proposition as part of the HR Advisor team, and supported the implementation and change management across the Cono Sur region, including stakeholder engagement and adoption strategy.",
      "es": "Participé en el rediseño del modelo operativo de RRHH y su propuesta de valor como parte del equipo de HR Advisor, y acompañé la implementación y la gestión del cambio en la región del Cono Sur, incluyendo el involucramiento de stakeholders y la estrategia de adopción."
    }
  },
  {
    "id": "eightfold-pilot",
    "categories": [
      "HR Technology",
      "Workforce Planning"
    ],
    "role": "R3",
    "year": "2021",
    "flagship": false,
    "name": {
      "en": "Eightfold AI Talent Platform Pilot",
      "es": "Piloto de la Plataforma de Talento con IA Eightfold"
    },
    "roleTag": {
      "en": "Piloted",
      "es": "Piloté"
    },
    "description": {
      "en": "Participated in the pilot of Eightfold, an AI-powered talent intelligence platform, exploring its application for recruitment, internal mobility, capability mapping and bias reduction in talent decisions.",
      "es": "Participé en el piloto de Eightfold, una plataforma de inteligencia de talento con IA, explorando su aplicación para reclutamiento, movilidad interna, mapeo de capacidades y reducción de sesgo en las decisiones de talento."
    }
  },
  {
    "id": "org-restructuring",
    "categories": [
      "Organizational Design"
    ],
    "role": "R5",
    "year": "2025",
    "flagship": false,
    "name": {
      "en": "Organizational Restructuring",
      "es": "Reestructuración Organizacional"
    },
    "roleTag": {
      "en": "Managed",
      "es": "Gestioné"
    },
    "description": {
      "en": "Managed the organizational redesign during a significant downsizing of 75 people, including role redefinition, communication and compliance across multiple regions.",
      "es": "Gestioné el rediseño organizacional durante una reducción significativa de 75 personas, incluyendo la redefinición de roles, comunicación y cumplimiento en múltiples regiones."
    }
  },
  {
    "id": "strategic-capability-development",
    "categories": [
      "Workforce Planning"
    ],
    "role": "R2",
    "year": "2016-2017",
    "flagship": false,
    "name": {
      "en": "Strategic Capability Development",
      "es": "Desarrollo Estratégico de Capacidades"
    },
    "roleTag": {
      "en": "Supported",
      "es": "Acompañé"
    },
    "description": {
      "en": "Partnered with a local school to develop internal technical talent in electrician and mechanic roles, reducing dependency on external hiring in hard-to-fill positions and fostering internal growth and engagement for employees from local communities. Later grew into an ongoing practice.",
      "es": "Trabajé junto a una escuela local para desarrollar talento técnico interno en roles de electricista y mecánico, reduciendo la dependencia de contratación externa en posiciones difíciles de cubrir e impulsando el crecimiento interno y el engagement de empleados de comunidades locales. Con el tiempo se convirtió en una práctica sostenida."
    }
  },
  {
    "id": "women-in-operations",
    "categories": [
      "Workforce Planning"
    ],
    "role": "R2",
    "year": "2016-2017",
    "flagship": false,
    "name": {
      "en": "Women in Operations Program (D&I)",
      "es": "Programa de Mujeres en Operaciones (D&I)"
    },
    "roleTag": {
      "en": "Led",
      "es": "Lideré"
    },
    "description": {
      "en": "Led a diversity and inclusion initiative to expand women's participation in industrial plant operations, identifying roles where female employees outperformed in precision tasks such as seed selection, measurably reducing waste on the production line while creating internal growth pathways for a historically underrepresented group.",
      "es": "Lideré una iniciativa de diversidad e inclusión para ampliar la participación de mujeres en operaciones de planta industrial, identificando roles donde el desempeño femenino era superior en tareas de precisión como la selección de semillas, reduciendo de forma medible el desperdicio en la línea de producción y creando caminos de crecimiento interno para un grupo históricamente subrepresentado."
    }
  },
  {
    "id": "seasonal-workforce-tool",
    "categories": [
      "Workforce Planning"
    ],
    "role": "R2",
    "year": "2017",
    "flagship": false,
    "name": {
      "en": "Seasonal Workforce Management Tool",
      "es": "Herramienta de Gestión de Personal de Temporada"
    },
    "roleTag": {
      "en": "Built",
      "es": "Construí"
    },
    "description": {
      "en": "Built a workforce management tool to plan and track seasonal demand across multiple fields, coordinating logistics including transportation, accommodation, safety training and onboarding for 3,000+ seasonal employees, and enabling cost estimation and budget variance analysis through an integrated dashboard. Built to support the annual seasonal planning cycle.",
      "es": "Construí una herramienta de gestión de personal para planificar y seguir la demanda de temporada en múltiples campos, coordinando logística de transporte, alojamiento, capacitación en seguridad e inducción para más de 3.000 empleados de temporada, y habilitando estimación de costos y análisis de variación presupuestaria en un dashboard integrado. Construida para apoyar el ciclo anual de planificación de temporada."
    }
  },
  {
    "id": "perf-mgmt-framework-design",
    "categories": [
      "Performance Management"
    ],
    "role": "R5",
    "year": "2024",
    "flagship": false,
    "name": {
      "en": "Performance Management Framework Design",
      "es": "Diseño del Marco de Gestión del Desempeño"
    },
    "roleTag": {
      "en": "Contributed",
      "es": "Contribuí"
    },
    "description": {
      "en": "Contributed to the design and implementation of a performance management framework from scratch in a fast-growing startup, including model definition, rollout and initial cycles.",
      "es": "Contribuí al diseño e implementación de un marco de gestión del desempeño desde cero en una startup en rápido crecimiento, incluyendo la definición del modelo, el lanzamiento y los primeros ciclos."
    }
  },
  {
    "id": "full-pms-oasis",
    "categories": [
      "Performance Management"
    ],
    "role": "R6",
    "year": "2026",
    "flagship": true,
    "name": {
      "en": "Full Performance Management System",
      "es": "Sistema Completo de Gestión del Desempeño"
    },
    "roleTag": {
      "en": "Designed",
      "es": "Diseñé"
    },
    "description": {
      "en": "Designed the complete performance management system from scratch, including a competency framework with 15 competencies across 5 proficiency levels, a potential assessment model based on learning agility, and the annual success management cycle.",
      "es": "Diseñé el sistema completo de gestión del desempeño desde cero, incluyendo un marco de competencias con 15 competencias en 5 niveles de dominio, un modelo de evaluación de potencial basado en agilidad de aprendizaje y el ciclo anual de gestión de éxito."
    }
  },
  {
    "id": "manager-enablement-program",
    "categories": [
      "Performance Management",
      "Leadership & Change"
    ],
    "role": "R5",
    "year": "2024",
    "flagship": false,
    "name": {
      "en": "Manager Enablement Program",
      "es": "Programa de Habilitación para Managers"
    },
    "roleTag": {
      "en": "Co-led",
      "es": "Colideré"
    },
    "description": {
      "en": "Co-designed and launched the first leadership development program for people managers, defining structure, content and rollout approach, and designed manager enablement resources and coaching support for 20+ people managers across 8 countries.",
      "es": "Codiseñé y lancé el primer programa de desarrollo de liderazgo para people managers, definiendo estructura, contenido y despliegue, y diseñé recursos de habilitación y coaching de soporte para más de 20 people managers en 8 países."
    }
  },
  {
    "id": "onboarding-experience",
    "categories": [
      "Employee Experience"
    ],
    "role": "R4",
    "year": "2023-2025",
    "flagship": false,
    "name": {
      "en": "Onboarding Experience Design",
      "es": "Diseño de la Experiencia de Onboarding"
    },
    "roleTag": {
      "en": "Designed",
      "es": "Diseñé"
    },
    "description": {
      "en": "Mapped and redesigned the end-to-end onboarding process to support scaling from 60 to 300 people, covering organic hiring, M&A integrations and internal role transitions, ensuring a consistent experience regardless of entry type.",
      "es": "Mapeé y rediseñé el proceso de onboarding de punta a punta para sostener el crecimiento de 60 a 300 personas, cubriendo contratación orgánica, integraciones por M&A y transiciones internas de rol, asegurando una experiencia consistente sin importar el tipo de ingreso."
    }
  },
  {
    "id": "offboarding-experience",
    "categories": [
      "Employee Experience"
    ],
    "role": "R6",
    "year": "2026",
    "flagship": false,
    "name": {
      "en": "Offboarding Experience Design",
      "es": "Diseño de la Experiencia de Offboarding"
    },
    "roleTag": {
      "en": "Designed",
      "es": "Diseñé"
    },
    "description": {
      "en": "Mapped the existing offboarding process and designed a structured, human-centered experience ensuring administrative compliance and a healthy exit for every departing employee, adapted to different locations and contract types.",
      "es": "Mapeé el proceso de offboarding existente y diseñé una experiencia estructurada y centrada en las personas, asegurando el cumplimiento administrativo y una salida saludable para cada empleado, adaptada a distintas ubicaciones y tipos de contrato."
    }
  },
  {
    "id": "compensation-framework",
    "categories": [
      "Employee Experience"
    ],
    "role": "R5",
    "year": "2024",
    "flagship": false,
    "name": {
      "en": "Compensation Framework",
      "es": "Marco de Compensaciones"
    },
    "roleTag": {
      "en": "Co-designed",
      "es": "Codiseñé"
    },
    "description": {
      "en": "Designed the compensation structure for a scaling startup, including job evaluation, salary banding and internal equity principles to support fair and consistent pay decisions across regions.",
      "es": "Diseñé la estructura de compensaciones para una startup en crecimiento, incluyendo evaluación de puestos, bandas salariales y principios de equidad interna para decisiones de pago justas y consistentes entre regiones."
    }
  },
  {
    "id": "people-share-scheme",
    "categories": [
      "Employee Experience"
    ],
    "role": "R5",
    "year": "2024",
    "flagship": false,
    "name": {
      "en": "People Share Scheme",
      "es": "Esquema de Participación de Personas"
    },
    "roleTag": {
      "en": "Co-designed",
      "es": "Codiseñé"
    },
    "description": {
      "en": "Designed and implemented a collaborative variable compensation scheme connecting individual and team performance to shared business outcomes, aligned with company values and retention objectives.",
      "es": "Diseñé e implementé un esquema colaborativo de compensación variable que conecta el desempeño individual y de equipo con los resultados del negocio, alineado con los valores de la compañía y los objetivos de retención."
    }
  },
  {
    "id": "humaans-implementation",
    "categories": [
      "HR Technology"
    ],
    "role": "R4",
    "year": "2023",
    "flagship": false,
    "name": {
      "en": "Humaans Implementation",
      "es": "Implementación de Humaans"
    },
    "roleTag": {
      "en": "Led",
      "es": "Lideré"
    },
    "description": {
      "en": "Led the implementation of Humaans as an interim HRIS solution, including configuration, workflows, permissions and adoption across the organization.",
      "es": "Lideré la implementación de Humaans como solución interina de HRIS, incluyendo configuración, flujos de trabajo, permisos y adopción en toda la organización."
    }
  },
  {
    "id": "cezanne-hibob-implementation",
    "categories": [
      "HR Technology",
      "Leadership & Change"
    ],
    "role": "R5",
    "year": "2024-2025",
    "flagship": false,
    "name": {
      "en": "Cezanne & HiBob Implementation",
      "es": "Implementación de Cezanne y HiBob"
    },
    "roleTag": {
      "en": "Led",
      "es": "Lideré"
    },
    "description": {
      "en": "Led the full implementation of Cezanne and then HiBob as the core HRIS platform from scratch, workflows, permissions, onboarding, offboarding, time off policies and reporting dashboards, including data migration and the change management process, stakeholder testing, change ambassador programs, early adopter identification and training delivery.",
      "es": "Lideré la implementación completa de Cezanne y luego de HiBob como plataforma central de HRIS desde cero, flujos de trabajo, permisos, onboarding, offboarding, políticas de tiempo libre y dashboards de reporting, incluyendo migración de datos y el proceso de gestión del cambio, pruebas con stakeholders, programas de embajadores del cambio, identificación de early adopters y capacitación."
    }
  },
  {
    "id": "travel-budget-system",
    "categories": [
      "HR Technology"
    ],
    "role": "R5",
    "year": "2024",
    "flagship": false,
    "name": {
      "en": "Travel & Budget Request System",
      "es": "Sistema de Solicitud de Viajes y Presupuesto"
    },
    "roleTag": {
      "en": "Built",
      "es": "Construí"
    },
    "description": {
      "en": "Built a Jira-based travel and budget request workflow including escalation logic, approval flows and tracking, replacing manual processes and giving finance and leadership visibility into spend.",
      "es": "Construí un flujo de solicitud de viajes y presupuesto en Jira, con lógica de escalamiento, flujos de aprobación y seguimiento, reemplazando procesos manuales y dando a finanzas y al liderazgo visibilidad del gasto."
    }
  },
  {
    "id": "people-helpdesk",
    "categories": [
      "HR Technology"
    ],
    "role": "R5",
    "year": "2023-2025",
    "flagship": false,
    "name": {
      "en": "People Helpdesk",
      "es": "Mesa de Ayuda de Personas"
    },
    "roleTag": {
      "en": "Designed",
      "es": "Diseñé"
    },
    "description": {
      "en": "Designed and implemented a Jira-based helpdesk for employees and managers to submit, track and resolve People queries collaboratively, improving response times and visibility across the team.",
      "es": "Diseñé e implementé una mesa de ayuda en Jira para que empleados y managers enviaran, siguieran y resolvieran consultas de Personas de forma colaborativa, mejorando los tiempos de respuesta y la visibilidad del equipo."
    }
  },
  {
    "id": "wa-dashboards",
    "categories": [
      "People Analytics"
    ],
    "role": "R5",
    "year": "2024",
    "flagship": false,
    "name": {
      "en": "WA Technology Dashboards",
      "es": "Dashboards de WA Technology"
    },
    "roleTag": {
      "en": "Built",
      "es": "Construí"
    },
    "description": {
      "en": "Built C-suite and organizational health dashboards in Power BI and Looker Studio connecting People data to business outcomes for a 300-person org, tracking team size, structure and key People indicators for two audiences, executive leadership and senior leadership more broadly. Ongoing maintenance and data health are part of the role's core responsibilities.",
      "es": "Construí dashboards para el C-suite y de salud organizacional en Power BI y Looker Studio, conectando los datos de personas con los resultados del negocio para una organización de 300 personas, siguiendo tamaño de equipo, estructura e indicadores clave de Personas para dos audiencias, el C-suite y el liderazgo senior en general. El mantenimiento continuo y la calidad de los datos son parte de las responsabilidades centrales del rol."
    }
  },
  {
    "id": "people-metrics-kpi",
    "categories": [
      "People Analytics"
    ],
    "role": "R5",
    "year": "2023-2025",
    "flagship": false,
    "name": {
      "en": "People Metrics & KPI Framework",
      "es": "Marco de Métricas y KPIs de Personas"
    },
    "roleTag": {
      "en": "Defined",
      "es": "Definí"
    },
    "description": {
      "en": "Defined the People metrics framework establishing what to measure, how to interpret it and what triggers action, including KPIs tracking headcount, attrition, time to hire, eNPS and engagement across regions.",
      "es": "Definí el marco de métricas de Personas, estableciendo qué medir, cómo interpretarlo y qué dispara una acción, incluyendo KPIs de dotación, rotación, time to hire, eNPS y engagement en todas las regiones."
    }
  },
  {
    "id": "ai-native-hr-platform",
    "categories": [
      "AI Product Design"
    ],
    "role": "R7",
    "year": "2025-present",
    "flagship": true,
    "name": {
      "en": "AI-Native HR Platform",
      "es": "Plataforma de RRHH Nativa en IA"
    },
    "roleTag": {
      "en": "Owned",
      "es": "Fui responsable de"
    },
    "description": {
      "en": "Building an AI-native HR platform from the ground up, as Product Owner, spanning product discovery, strategy and roadmap, agent design and orchestration. Currently developing the recruitment module with a fully agentic, ATS-agnostic layer built around a central AI Orchestrator, having led the initial product discovery and defined the platform's phased strategy and go-to-market approach.",
      "es": "Construyo una plataforma de RRHH nativa en IA desde cero, como Product Owner, abarcando discovery de producto, estrategia y roadmap, y diseño y orquestación de agentes. Actualmente desarrollando el módulo de reclutamiento con una capa totalmente agéntica, agnóstica de ATS, construida alrededor de un Orquestador de IA central, habiendo liderado el discovery inicial del producto y definido la estrategia por fases y el enfoque de go to market de la plataforma."
    }
  },
  {
    "id": "oasis-engagement-platform",
    "categories": [
      "AI Product Design",
      "People Strategy",
      "Employee Experience",
      "People Analytics"
    ],
    "role": "R6",
    "year": "2026",
    "flagship": true,
    "name": {
      "en": "Oasis Engagement Platform",
      "es": "Plataforma de Engagement de Oasis"
    },
    "roleTag": {
      "en": "Designed",
      "es": "Diseñé"
    },
    "description": {
      "en": "Owned the engagement platform end to end for a 16-person startup: designed the engagement strategy and listening cadence, the full process including data collection methodology and a dual-cycle model, the sentiment and eNPS analytics, and co-built the AI agent that conducts engagement check-ins, capturing quantitative scores and qualitative context in a single interaction.",
      "es": "Fui responsable de la plataforma de engagement de punta a punta para una startup de 16 personas: diseñé la estrategia de engagement y el ritmo de escucha, el proceso completo incluyendo la metodología de recolección de datos y un modelo de doble ciclo, la analítica de sentimiento y eNPS, y co-construí el agente de IA que realiza los check-ins de engagement, capturando puntajes cuantitativos y contexto cualitativo en una sola interacción."
    }
  }
];

export const CATEGORY_LABELS = {
  en: {
    "People Strategy": "People Strategy",
    "Organizational Design": "Organizational Design",
    "Workforce Planning": "Workforce Planning",
    "Performance Management": "Performance Management",
    "Employee Experience": "Employee Experience",
    "HR Technology": "HR Technology",
    "People Analytics": "People Analytics",
    "Leadership & Change": "Leadership & Change",
    "AI Product Design": "AI Product Design",
  },
  es: {
    "People Strategy": "Estrategia de Personas",
    "Organizational Design": "Diseño Organizacional",
    "Workforce Planning": "Planificación de Personal",
    "Performance Management": "Gestión del Desempeño",
    "Employee Experience": "Experiencia del Empleado",
    "HR Technology": "Tecnología de RRHH",
    "People Analytics": "People Analytics",
    "Leadership & Change": "Liderazgo y Cambio",
    "AI Product Design": "Diseño de Producto de IA",
  },
};

// ---------------------------------------------------------------------------
// Full bilingual content bundle.
// ---------------------------------------------------------------------------

const EN = {
  nav: {
    home: "Home", about: "About", myWork: "My Work", currentProduct: "Current Product",
    vision: "Vision", knowledgeHub: "Knowledge Hub", contact: "Contact", downloadCv: "Download CV",
  },
  hero: {
    overline: "People Strategy, Culture & Technology",
    headline: { line1: "Building the People Infrastructure", line2Prefix: "That Makes Companies ", line2Highlight: "Scale", line3Prefix: "and People ", line3Highlight: "Grow." },
    subtitle: "Looking at the future of work.",
    description: "I combine more than 15 years of experience in People Strategy, Organizational Design and HR Technology with Product Thinking and Artificial Intelligence to design intelligent solutions for modern organizations.",
    exploreBtn: "Explore my work",
    productBtn: "Current Product",
  },
  about: {
    label: "About",
    titlePrefix: "My ", titleHighlight: "Journey",
    intro: "Every step of my career has shaped how I design people experiences and AI powered products today. From building People strategies and organizational models to designing HR technology and AI solutions, each experience contributes to the way I approach modern HR.",
    stageLabel: "Stage",
    journey: [
      { n: "01", title: "People Strategy", tagline: "Building organizations before building products.", body: "Designing scalable People strategies, workforce planning models, organizational structures, leadership frameworks and employee experiences across international organizations.", label: "What I built", items: ["Workforce Planning", "Organizational Design", "Performance Management", "Talent Strategy", "Employee Experience", "Leadership Frameworks"] },
      { n: "02", title: "HR Technology & Operations", tagline: "Turning people strategies into scalable systems.", body: "Designing HR processes, implementing technology, automating workflows and building data driven operating models that improve efficiency and employee experience.", label: "What I built", items: ["HRIS Implementations", "Process Automation", "People Analytics", "HR Operations", "Employee Journeys", "Dashboard & Reporting"] },
      { n: "03", title: "AI Product Design", tagline: "Designing the next generation of HR experiences.", body: "Bringing years of HR expertise to build conversational AI products that simplify complex people processes through intelligent agents, automation and product thinking.", label: "What I'm building", items: ["AI Powered People Products", "AI Agents", "Intelligent Workflows", "People Process Automation", "Product Strategy", "Human Centric AI Experiences"] },
    ],
    quote: "My passion is creating technology that makes HR more intelligent, more human and more impactful.",
  },
  experience: {
    label: "My Work",
    titlePrefix: "My ", titleHighlight: "Work",
    subtitle: "Seven roles, one throughline: turning people strategy into systems that scale.",
    keyProjectsLabel: "Key Projects",
    skillsLabel: "Skills exercised",
    flagshipLabel: "Flagship project",
    presentLabel: "Present",
  },
  product: {
    label: "Current Product",
    badge: "Currently in Development",
    titlePrefix: "AI Employee ", titleHighlight: "Engagement", titleSuffix: " Platform",
    description: "An AI powered employee engagement platform built around conversational agents that continuously measure employee sentiment, generate meaningful insights and recommend personalized actions. The goal is to move beyond traditional engagement surveys and create an ongoing conversation between employees, managers and organizations.",
    archLabel: "Product Vision — Architecture",
    flow: ["Employee", "Conversational AI", "AI Orchestrator", "Specialized Engagement Agents"],
    featuresLabel: "Product Features",
    techLabel: "Technology",
    agents: [
      { name: "Pulse Survey Agent", desc: "Runs conversation-based pulse surveys, adapting questions to context instead of static forms." },
      { name: "Sentiment Analysis Agent", desc: "Continuously measures employee sentiment from natural-language interactions and detects emerging signals." },
      { name: "Manager Insights Agent", desc: "Translates signals into clear, actionable recommendations for managers at the right moment." },
      { name: "Action Planning Agent", desc: "Generates personalized action plans and tracks follow-through across teams." },
      { name: "Knowledge Assistant", desc: "Retrieves policies, playbooks and context through RAG so answers are grounded and trustworthy." },
    ],
    features: ["Conversation based pulse surveys", "Natural language interactions", "AI generated insights", "Manager recommendations", "Trend analysis", "Personalized action plans", "Real time dashboards", "Continuous listening", "Knowledge retrieval", "Future integrations"],
    techStack: [
      { label: "Frontend", items: ["Next.js"] },
      { label: "Backend", items: ["Node 24"] },
      { label: "Artificial Intelligence", items: ["LLMs", "Conversational Agents", "MCP", "Vector Search", "RAG"] },
      { label: "Future integrations", items: ["HRIS", "Slack", "Microsoft Teams", "Google Workspace"] },
    ],
  },
  vision: {
    label: "The Long-Term Vision",
    titlePrefix: "Building an AI Native ", titleHighlight: "People", titleSuffix: " Platform",
    description: "The Employee Engagement Platform is the first product in a broader vision. Over time it will evolve into an ecosystem of specialized AI agents supporting every stage of the employee lifecycle.",
    centerLabel: "Center",
    orchestratorName: "AI Orchestrator",
    comingSoon: "Coming Soon",
    modules: [
      { name: "Employee Engagement", status: "Active Development", state: "active" },
      { name: "Recruitment", status: "Research", state: "research" },
      { name: "Learning", status: "Concept", state: "concept" },
      { name: "Performance", status: "Concept", state: "concept" },
      { name: "Workforce Planning", status: "Research", state: "research" },
      { name: "HR Operations", status: "Concept", state: "concept" },
      { name: "People Analytics", status: "Future", state: "future" },
      { name: "Manager Copilot", status: "Future", state: "future" },
    ],
  },
  contact: {
    label: "Contact",
    titlePrefix: "Let’s ", titleHighlight: "connect", titleSuffix: ".",
    links: [{ label: "LinkedIn" }, { label: "Email" }, { label: "Download CV" }],
    calendlyNote: "Future Calendly integration coming soon — for now, reach me directly at",
    footerTagline: "Building the future of People",
    adminLabel: "Admin",
  },
  knowledge: {
    label: "Knowledge Hub",
    titlePrefix: "Ideas, playbooks & ", titleHighlight: "documents", titleSuffix: ".",
    subtitle: "A growing library of articles and downloadable resources on People, HR technology and AI product design.",
    searchPlaceholder: "Search articles, templates, tags...",
    allLabel: "All",
    emptyTitle: "Nothing here yet",
    emptyBody: "Documents and articles will appear here as they are published.",
    topics: KNOWLEDGE_TOPIC_KEYS.map((k) => ({ key: k, label: k })),
  },
  marquee: ["Innovation", "Strategic Thinking", "Product Mindset", "Technical Curiosity", "Business Understanding", "Human-Centered Design"],
};

const ES = {
  nav: {
    home: "Inicio", about: "Sobre mí", myWork: "Mi Trabajo", currentProduct: "Producto Actual",
    vision: "Visión", knowledgeHub: "Centro de Conocimiento", contact: "Contacto", downloadCv: "Descargar CV",
  },
  hero: {
    overline: "Estrategia de Personas, Cultura y Tecnología",
    headline: { line1: "Construyendo la Infraestructura de Personas", line2Prefix: "Que Hace que las Empresas ", line2Highlight: "Escalen", line3Prefix: "y las Personas ", line3Highlight: "Crezcan." },
    subtitle: "Mirando hacia el futuro del trabajo.",
    description: "Combino más de 15 años de experiencia en Estrategia de Personas, Diseño Organizacional y Tecnología de RRHH con Pensamiento de Producto e Inteligencia Artificial para diseñar soluciones inteligentes para organizaciones modernas.",
    exploreBtn: "Ver mi trabajo",
    productBtn: "Producto Actual",
  },
  about: {
    label: "Sobre mí",
    titlePrefix: "Mi ", titleHighlight: "Trayectoria",
    intro: "Cada etapa de mi carrera dio forma a cómo diseño hoy experiencias de personas y productos con IA. Desde construir estrategias de Personas y modelos organizacionales hasta diseñar tecnología de RRHH y soluciones de IA, cada experiencia aporta a mi forma de entender el RRHH moderno.",
    stageLabel: "Etapa",
    journey: [
      { n: "01", title: "Estrategia de Personas", tagline: "Construyendo organizaciones antes de construir productos.", body: "Diseño estrategias de People escalables, modelos de planificación de la fuerza laboral, estructuras organizativas, marcos de liderazgo y experiencias de empleados en organizaciones internacionales.", label: "Lo que construí", items: ["Planificación de Personal", "Diseño Organizacional", "Gestión del Desempeño", "Estrategia de Talento", "Experiencia del Empleado", "Marcos de Liderazgo"] },
      { n: "02", title: "Tecnología y Operaciones de RRHH", tagline: "Convirtiendo estrategias de personas en sistemas escalables.", body: "Diseño procesos de RRHH, implemento tecnología, automatizo flujos de trabajo y construyo modelos operativos basados en datos que mejoran la eficiencia y la experiencia del empleado.", label: "Lo que construí", items: ["Implementaciones de HRIS", "Automatización de Procesos", "People Analytics", "Operaciones de RRHH", "Employee Journeys", "Dashboards y Reporting"] },
      { n: "03", title: "Diseño de Producto de IA", tagline: "Diseñando la próxima generación de experiencias de RRHH.", body: "Aporto años de experiencia en RRHH para construir productos de IA conversacional que simplifican procesos de personas complejos a través de agentes inteligentes, automatización y pensamiento de producto.", label: "Lo que estoy construyendo", items: ["Productos de Personas Potenciados con IA", "Agentes de IA", "Flujos de Trabajo Inteligentes", "Automatización de Procesos de Personas", "Estrategia de Producto", "Experiencias de IA Centradas en las Personas"] },
    ],
    quote: "Mi pasión es crear tecnología que haga que RRHH sea más inteligente, más humano y de mayor impacto.",
  },
  experience: {
    label: "Mi Trabajo",
    titlePrefix: "Mi ", titleHighlight: "Trabajo",
    subtitle: "Siete roles, un mismo hilo conductor: convertir la estrategia de Personas en sistemas que escalan.",
    keyProjectsLabel: "Proyectos Clave",
    skillsLabel: "Habilidades ejercidas",
    flagshipLabel: "Proyecto insignia",
    presentLabel: "Presente",
  },
  product: {
    label: "Producto Actual",
    badge: "En Desarrollo",
    titlePrefix: "Plataforma de ", titleHighlight: "Compromiso", titleSuffix: " del Empleado con IA",
    description: "Una plataforma de compromiso del empleado potenciada con IA, construida alrededor de agentes conversacionales que miden de forma continua el sentimiento de los empleados, generan insights significativos y recomiendan acciones personalizadas. El objetivo es ir más allá de las encuestas de engagement tradicionales y crear una conversación continua entre empleados, managers y organizaciones.",
    archLabel: "Visión del Producto: Arquitectura",
    flow: ["Empleado", "IA Conversacional", "Orquestador de IA", "Agentes de Engagement Especializados"],
    featuresLabel: "Funcionalidades del Producto",
    techLabel: "Tecnología",
    agents: [
      { name: "Agente de Encuestas de Pulso", desc: "Ejecuta encuestas de pulso basadas en conversación, adaptando las preguntas al contexto en lugar de usar formularios estáticos." },
      { name: "Agente de Análisis de Sentimiento", desc: "Mide de forma continua el sentimiento de los empleados a partir de interacciones en lenguaje natural y detecta señales emergentes." },
      { name: "Agente de Insights para Managers", desc: "Traduce las señales en recomendaciones claras y accionables para los managers en el momento justo." },
      { name: "Agente de Planes de Acción", desc: "Genera planes de acción personalizados y hace seguimiento de su cumplimiento en los equipos." },
      { name: "Asistente de Conocimiento", desc: "Recupera políticas, playbooks y contexto mediante RAG para que las respuestas sean confiables y estén bien fundamentadas." },
    ],
    features: ["Encuestas de pulso basadas en conversación", "Interacciones en lenguaje natural", "Insights generados por IA", "Recomendaciones para managers", "Análisis de tendencias", "Planes de acción personalizados", "Dashboards en tiempo real", "Escucha continua", "Recuperación de conocimiento", "Integraciones futuras"],
    techStack: [
      { label: "Frontend", items: ["Next.js"] },
      { label: "Backend", items: ["Node 24"] },
      { label: "Inteligencia Artificial", items: ["LLMs", "Agentes Conversacionales", "MCP", "Búsqueda Vectorial", "RAG"] },
      { label: "Integraciones futuras", items: ["HRIS", "Slack", "Microsoft Teams", "Google Workspace"] },
    ],
  },
  vision: {
    label: "La Visión a Largo Plazo",
    titlePrefix: "Construyendo una Plataforma de ", titleHighlight: "Personas", titleSuffix: " Nativa en IA",
    description: "La Plataforma de Employee Engagement es el primer producto de una visión más amplia. Con el tiempo evolucionará hacia un ecosistema de agentes de IA especializados que acompañan cada etapa del ciclo de vida del empleado.",
    centerLabel: "Centro",
    orchestratorName: "Orquestador de IA",
    comingSoon: "Próximamente",
    modules: [
      { name: "Employee Engagement", status: "Desarrollo Activo", state: "active" },
      { name: "Reclutamiento", status: "Investigación", state: "research" },
      { name: "Aprendizaje", status: "Concepto", state: "concept" },
      { name: "Desempeño", status: "Concepto", state: "concept" },
      { name: "Planificación de Personal", status: "Investigación", state: "research" },
      { name: "Operaciones de RRHH", status: "Concepto", state: "concept" },
      { name: "People Analytics", status: "Futuro", state: "future" },
      { name: "Copiloto para Managers", status: "Futuro", state: "future" },
    ],
  },
  contact: {
    label: "Contacto",
    titlePrefix: "", titleHighlight: "Conectemos", titleSuffix: ".",
    links: [{ label: "LinkedIn" }, { label: "Correo" }, { label: "Descargar CV" }],
    calendlyNote: "Próximamente se sumará la integración con Calendly. Mientras tanto, puedes escribirme directamente a",
    footerTagline: "Construyendo el futuro de las Personas",
    adminLabel: "Admin",
  },
  knowledge: {
    label: "Centro de Conocimiento",
    titlePrefix: "Ideas, guías y ", titleHighlight: "documentos", titleSuffix: ".",
    subtitle: "Una biblioteca creciente de artículos y recursos descargables sobre Personas, tecnología de RRHH y diseño de producto con IA.",
    searchPlaceholder: "Buscar artículos, plantillas, etiquetas...",
    allLabel: "Todos",
    emptyTitle: "Todavía no hay nada aquí",
    emptyBody: "Los documentos y artículos aparecerán aquí a medida que se publiquen.",
    topics: [
      { key: "People Strategy", label: "Estrategia de Personas" },
      { key: "Performance Management", label: "Gestión del Desempeño" },
      { key: "Workforce Planning", label: "Planificación de Personal" },
      { key: "Organizational Design", label: "Diseño Organizacional" },
      { key: "Employee Experience", label: "Experiencia del Empleado" },
      { key: "HR Technology", label: "Tecnología de RRHH" },
      { key: "Artificial Intelligence", label: "Inteligencia Artificial" },
      { key: "Product Design", label: "Diseño de Producto" },
      { key: "Research Notes", label: "Notas de Investigación" },
      { key: "Templates", label: "Plantillas" },
      { key: "Playbooks", label: "Playbooks" },
      { key: "Case Studies", label: "Casos de Estudio" },
      { key: "Presentations", label: "Presentaciones" },
    ],
  },
  marquee: ["Innovación", "Pensamiento Estratégico", "Mentalidad de Producto", "Curiosidad Técnica", "Comprensión del Negocio", "Diseño Centrado en las Personas"],
};

export const CONTENT = { en: EN, es: ES };
